import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import FitScoreLogo from '../components/FitScoreLogo';
import './About.css';
import '../ScrollAnimations.css';

function About() {
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
        }
      });
    }, observerOptions);

    document.querySelectorAll('.fade-in-up, .stagger-item').forEach(el => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);
  return (
    <div className="about-page">
      <section className="about-hero" style={{
        background: 'linear-gradient(135deg, #FF6B35 0%, #e55a2b 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Option 3: Pattern of small logos */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          opacity: 0.08,
          backgroundImage: 'url(/fit-logo.jpg)',
          backgroundSize: '150px 150px',
          backgroundRepeat: 'repeat',
          zIndex: 0
        }}></div>
        <div className="container" style={{position: 'relative', zIndex: 1, textAlign: 'center'}}>
          <div style={{backgroundColor: '#000', display: 'inline-block', padding: '20px 40px', borderRadius: '8px'}}>
            <h1 style={{color: 'white', margin: 0}}>About <span className="fit-inline"><span className="f-orange" style={{color: '#FF6B35'}}>F</span>-IT</span> Score</h1>
          </div>
          <p className="hero-tagline" style={{color: '#2c2c2c', fontWeight: 'bold', fontStyle: 'italic', marginTop: '20px', fontSize: '1.1em'}}>Born from lived experience, built on psychology, driven by passion</p>
          <p className="lead" style={{margin: '20px auto 0'}}>Meet the team behind the movement transforming workplace wellbeing & safety</p>
        </div>
      </section>

      {/* The F-IT Team */}
      <section className="team-section">
        <div className="container">
          <h2 className="section-title">The <span className="fit-inline"><span className="f-orange">F</span>-IT</span> Team</h2>
          <p className="section-intro">Three people united by lived experience and a shared mission<br/>to prevent others from experiencing what&nbsp;they&nbsp;did</p>
          
          {/* Jason Anker MBE */}
          <div className="team-member orange-accent fade-in-up">
            <div className="member-header">
              <h3>Jason Anker MBE</h3>
              <p className="member-title">Founder & Speaker</p>
            </div>
            <div className="member-content">
              <img src="https://customer-assets.emergentagent.com/job_fitmarketing-1/artifacts/seuzabou_Vivo243%20%281%29%20jpeg.jpeg" alt="Jason Anker MBE" className="member-profile-image" />
              <p>Jason has been speaking professionally for over 16 years, however his message today is more powerful than ever. His unique story is both harrowing and uplifting, exploring the critical connections between Wellbeing, Health & Safety - and how they influence one another, for better or worse.</p>
              
              <h4>The Fall</h4>
              <p>Jason openly shares the story of his 1993 workplace accident and its devastating ripple effect - not just on him, however on his family, friends, and work colleagues. More importantly, he now explores the question that haunted him for decades: <strong>WHY did he ignore his safety concerns that&nbsp;day?</strong></p>
              
              <p>For years, Jason blamed his choices, his inexperience, his youthful belief that "it would never happen to me." However the real answer ran much deeper. In the year leading up to his accident, Jason's wellbeing and mental health were in crisis. He was struggling with a job he hated, drowning in debt, and facing marriage problems. <strong>His compromised wellbeing was the significant contributory reason behind that critical poor choice.</strong></p>
              
              <p>In 2015, Jason started using the phrase "F**k it" in his presentations to capture that moment - that split second when someone's wellbeing is so compromised they make a dangerous decision. A decade later, this phrase is making a profound impact worldwide.</p>
              
              <h4>The Bounce</h4>
              <p>After nearly 25 years of merely coping with anxiety, stress, and mental health challenges, Jason discovered something remarkable: simple changes in his day-to-day life could have a transformative impact. Through understanding his own journey and making intentional shifts, Jason found the resilience not just to survive, however to <strong>thrive</strong> in all aspects of his life.</p>
              
              <p>Today, Jason uses his lived experience to help organisations understand the vital connection between employee wellbeing and workplace safety - because when people feel better, they make better&nbsp;decisions.</p>
            </div>
          </div>

          {/* Abbi Taylor */}
          <div className="team-member orange-accent fade-in-up">
            <div className="member-header">
              <h3>Abbi Taylor</h3>
              <p className="member-title">Managing Director, P2bS</p>
            </div>
            <div className="member-content">
              <img src="https://customer-assets.emergentagent.com/job_fitmarketing-1/artifacts/pa9qr3i5_Abbi%20bio%20pic%202025%20%281%29%20smaller.jpeg" alt="Abbi Taylor" className="member-profile-image" />
              <p>Abbi is the Managing Director and driving force behind P2bS. While she rarely speaks on the speaking circuit herself, her leadership and vision have been instrumental in shaping the organisation's mission and growth.</p>
              
              <p>Abbi brings a powerful and deeply personal perspective to the <span className="fit-inline"><span className="f-orange">F</span>-IT</span> story - she is Jason's daughter, and she lived through the devastating aftermath of his 1993 workplace accident.</p>
              
              <p>Abbi speaks with raw emotion about growing up with a father whose life was forever changed by one unsafe decision. She shares the experiences she missed out on, the moments that could never be reclaimed. Now, as a mother of two daughters herself, that pain has come full circle. Jason cannot do with his granddaughters what he missed doing with Abbi - and the family is reliving that loss all over again.</p>
              
              <p>Her message to organisations is clear and unforgettable: <strong>It's not worth it.</strong> If something feels wrong or unsafe, speak up - not just for yourself, however for your family and friends. They're the ones who carry the&nbsp;consequences.</p>
              
              <h4>A New Understanding, A New Mission</h4>
              <p>For years, Abbi lived with the effects of her father's accident without fully understanding <em>why</em> it happened. Now, knowing about the mental health crisis that led to Jason's "F**k it" moment - the compromised wellbeing, the overwhelming stress, the feeling of having no choice - she sees the bigger picture.</p>
              
              <p>This understanding transformed her pain into purpose. Abbi has become a driving force behind <span className="fit-inline"><span className="f-orange">F</span>-IT</span> Score, working tirelessly to expand its reach and impact. She's witnessed firsthand how Jason's improved mental wellbeing has positively transformed their family, inspiring her to make meaningful changes in her own life.</p>
              
              <p>Today, Abbi is passionate about ensuring no other family has to experience what hers did - and that starts with helping organisations recognise and address wellbeing issues before they lead to&nbsp;tragedy.</p>
            </div>
          </div>

          {/* Sam Anker */}
          <div className="team-member orange-accent fade-in-up">
            <div className="member-header">
              <h3>Sam Anker</h3>
              <p className="member-title">Social Media Manager & F-IT Logo Designer</p>
            </div>
            <div className="member-content">
              <img src="https://customer-assets.emergentagent.com/job_fit-showcase-1/artifacts/d7ph2j32_481959079_2093149831123613_182807668885895240_n.jpg" alt="Sam Anker" className="member-profile-image" />
              <p>Sam was barely one year old when his father's workplace accident changed the trajectory of their family forever. He has no memory of life before that day - only the reality of growing up with a father whose injuries meant he couldn't do the things other dads did.</p>
              
              <p>Jason tried. He truly tried. However something as simple as playing football - a passion they both shared - became another painful reminder of what the accident had taken away. These small, everyday losses amplified the true cost of workplace incidents in ways most people never realise.</p>
              
              <h4>The Unspoken Years</h4>
              <p>As Sam entered his teenage years, the unspoken weight of the accident created distance between father and son. Their relationship became strained. Sam moved out. They never talked about their feelings - not about the accident, not about each other, not about the pain they were both carrying.</p>
              
              <p>For a time, Sam travelled with Jason to his presentations, even speaking candidly about the impact on his own life. However reliving that trauma proved too much. The toll on Sam's own mental health became unbearable, and he had to step away.</p>
              
              <h4>A New Chapter</h4>
              <p>Today, Sam and Jason are closer than ever. The turning point? Watching his father finally turn his life around. Seeing Jason openly discuss his mental health struggles - without shame, without hiding - inspired Sam to look at his own life differently.</p>
              
              <p>This transformation ignited a passion. Sam became the Social Media Manager for <span className="fit-inline"><span className="f-orange">F</span>-IT</span> Score and P2bS, channelling his lived experience into expanding the reach of a message he deeply believes in. His creativity and vision led him to design the <span className="fit-inline"><span className="f-orange">F</span>-IT</span> logo itself - a symbol now recognised by organisations around the world.</p>
              
              <p>Sam's journey from pain to purpose embodies what <span className="fit-inline"><span className="f-orange">F</span>-IT</span> Score stands for: when we address mental health and wellbeing openly, we don't just prevent accidents - we heal families, rebuild relationships, and create lasting&nbsp;change.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Prof Tim Marsh */}
      <section className="expert-section">
        <div className="container">
          <h2 className="section-title">The Psychology Expert</h2>
          
          <div className="team-member blue-accent fade-in-up">
            <div className="member-header">
              <h3>Professor Tim Marsh</h3>
              <p className="member-title">World Authority on Human Error & Organisational Culture</p>
            </div>
            <div className="member-content">
              <img src="https://customer-assets.emergentagent.com/job_premium-fit-site/artifacts/1hibw3nf_tim.jpeg" alt="Professor Tim Marsh" className="member-profile-image" />
              <p>Tim is considered a world authority on the subject of human error and organisational culture and has given keynote talks around the world – including Africa, New Zealand, The Far East and the Middle East as well as throughout Europe and the USA. He was the closing keynote at the first ever Campbell Institute Thought Leadership conference in Dallas, Texas in 2015 and has published several best-selling books.</p>
              
              <p>In recent years Tim has returned to where he first started – mental health at work. (His first job in the early 1990s was looking at suicide in young army recruits).</p>
              
              <h4>Understanding <span className="fit-inline"><span className="f-blue">F</span>-YOU</span></h4>
              <p>Tim's <span className="fit-inline"><span className="f-blue">F</span>-YOU</span> presentation and workshop draw on his extensive academic knowledge and proven individual and organisational resilience methodologies that make individuals and workplaces flourish and thrive.</p>
              
              <p className="tim-quote">"Through good communication it is possible to talk with someone with a high <span className="fit-inline"><span className="f-orange">F</span>-IT</span> score. However if companies allow this to turn into a high <span className="fit-inline"><span className="f-blue">F</span>-YOU</span> score, where an individual nods his head, signs the sheets then calls you under his breath as he walks away, then you have real problems."</p>
              
              <p>Be assured, however, that his talks are humorous, highly interactive and user-friendly – illustrated with famous quotes, case studies and sporting analogies. In short, everything you could ever need to know about why people screw up – and what we as individuals (and organisations) can do to stop that happening.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="mission-section">
        <div className="container">
          <h2 className="section-title fade-in-up">Our Mission</h2>
          <div className="mission-content fade-in-up">
            <p className="mission-statement" style={{fontSize: '1.15em'}}>
              Our mission is to prevent anyone, in any workplace, from reaching that critical "F**k it" moment where compromised wellbeing leads to dangerous decisions. We exist to measure, understand, and address the wellbeing issues that create these moments - before they happen. By connecting individual wellbeing to workplace safety, we're not just preventing accidents; we're saving lives, protecting families, and building cultures where people never have to choose between their wellbeing and their livelihood. This isn't another corporate initiative that fades - this is real, honest, and permanent change.
            </p>
            
            {/* Maya Angelou Quote */}
            <div className="mission-quote fade-in-up">
              <div className="mission-quote-image">
                <img src="https://customer-assets.emergentagent.com/job_fitmarketing-1/artifacts/2nn5yplt_MAYA%201.jpeg" alt="Maya Angelou" />
              </div>
              <div className="mission-quote-content">
                <p className="quote-text">"People will forget what you said, people will forget what you did, but people will never forget how you made them feel"</p>
                <p className="quote-author">— Maya Angelou</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="family-connection">
        <div className="container">
          <h2 className="section-title">Part of the <span className="fit-inline"><span className="f-orange">F</span>-IT</span> Family</h2>
          
          <div className="family-grid">
            <div className="family-card orange-family-card">
              <div className="family-logo">
                <img src="https://customer-assets.emergentagent.com/job_fitmarketing-1/artifacts/1qt7dwmn_log3.png" alt="P2bS Logo" />
              </div>
              <h3>P2bS</h3>
              <p className="family-subtitle">Safety, Health & Wellbeing Speakers</p>
              <p><strong>P2bS</strong> is home to some of the world's most impactful motivational speakers on Safety, Health, and Wellbeing. Our speakers share real stories of adversity, resilience, and transformation - inspiring companies worldwide to prioritize people over process.</p>
              
              <p><span className="fit-inline"><span className="f-orange">F</span>-IT</span> Score is a natural extension of this mission: giving companies practical tools to turn inspiration into measurable, sustainable culture change.</p>
              
              <a href="https://www.p2bs.org" target="_blank" rel="noopener noreferrer" className="btn btn-orange">Visit P2bS</a>
            </div>
            
            <div className="family-card blue-family-card">
              <div className="family-logo">
                <img src="https://customer-assets.emergentagent.com/job_fitmarketing-1/artifacts/uy922xt7_anker%20%26%20marsh%20logo%20BEST%20%282%29.jpg" alt="Anker & Marsh Logo" />
              </div>
              <h3>Anker & Marsh Ltd</h3>
              <p className="family-subtitle">Organisational Psychology & Behavioural Safety</p>
              <p><strong>Anker & Marsh</strong> combines Jason Anker's lived experience with Professor Tim Marsh's academic expertise in human error and organisational culture. Together, they deliver world-class training, workshops, and consultancy that transforms how organisations understand and address workplace behaviour.</p>
              
              <p>Their unique partnership brings authenticity and academic rigour to <span className="fit-inline"><span className="f-blue">F</span>-YOU</span> - measuring organisational culture and response to employee wellbeing.</p>
              
              <a href="http://www.ankerandmarsh.com" target="_blank" rel="noopener noreferrer" className="btn btn-blue">Visit Anker & Marsh</a>
            </div>
          </div>
        </div>
      </section>

      <section className="cta-section">
        <div className="container cta-box">
          <h2>Ready to Make Wellbeing Measurable?</h2>
          <p>Discover how F-IT Score can transform your organisation's approach to employee&nbsp;wellbeing</p>
          <div className="cta-buttons">
            <Link to="/demo" className="btn btn-orange">Request a Demo</Link>
            <Link to="/how-it-works" className="btn btn-outline">Learn How It Works</Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default About;
